import classNames from "classnames";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { ChevronRight } from "react-ikonate";
import { FaBars } from "react-icons/fa";
import { useToggle } from "react-use";
import styles from "./CategoryListItem.module.css";
import { OrderControls } from "../OrderControls/OrderControls";

/**
 * Interactive component. Displays a category and provides controls for interacting with it.
 */
export function CategoryListItem({
  isFirst,
  isLast,
  onReorder,
  dragProps,
  isDragging,
  dropPosition,
  onReorderKeyDown,
  content,
  controls,
  subcategoriesCount,
  renderSubcategories,
  compactLayout = false,
}) {
  const [isExpanded, toggleExpanded] = useToggle(false);
  const hasSubcategories =
    subcategoriesCount > 0 && renderSubcategories != null;

  useEffect(() => {
    if (!hasSubcategories) toggleExpanded(false);
  }, [hasSubcategories, toggleExpanded]);

  return (
    <li
      className={classNames(styles.container, {
        [styles.compactLayout]: compactLayout,
      })}
    >
      <div
        {...dragProps}
        className={classNames(styles.main, {
          [styles.draggable]: dragProps?.draggable,
          [styles.dragging]: isDragging,
          [styles.dropAbove]: dropPosition === "above",
          [styles.dropBelow]: dropPosition === "below",
          [styles.dropInside]: dropPosition === "inside",
        })}
      >
        {(dragProps?.draggable || onReorder) && (
          <div>
            <div className={styles.orderControls}>
              {dragProps?.draggable ? (
                <span
                  className={styles.dragHandle}
                  role="button"
                  tabIndex={0}
                  title="Drag to reorder category"
                  aria-label="Reorder category"
                  onKeyDown={onReorderKeyDown}
                >
                  <FaBars aria-hidden="true" />
                </span>
              ) : (
                <OrderControls
                  isFirst={isFirst}
                  isLast={isLast}
                  onPrevious={() => {
                    onReorder(-1);
                  }}
                  onNext={() => {
                    onReorder(+1);
                  }}
                />
              )}
            </div>
          </div>
        )}

        <div className={styles.content} data-category-content>
          {content}
          {subcategoriesCount > 0 && (
            <span className={styles.countBadge}>{subcategoriesCount}</span>
          )}
        </div>

        <div className={styles.controls}>
          {controls}

          <button
            className={classNames(
              styles.expandToggle,
              hasSubcategories && isExpanded && styles.open
            )}
            disabled={!hasSubcategories}
            aria-expanded={hasSubcategories && isExpanded}
            aria-label={
              isExpanded ? "Collapse subcategories" : "Expand subcategories"
            }
            onClick={toggleExpanded}
          >
            <ChevronRight className={styles.expandIcon} />
          </button>
        </div>
      </div>

      {hasSubcategories && isExpanded && (
        <div className={styles.subcategories}>{renderSubcategories()}</div>
      )}
    </li>
  );
}

CategoryListItem.propTypes = {
  dragProps: PropTypes.object,
  isDragging: PropTypes.bool,
  dropPosition: PropTypes.oneOf(["above", "below", "inside"]),
  onReorderKeyDown: PropTypes.func,
  /**
   * Should be true if this is the first item in the list
   */
  isFirst: PropTypes.bool,

  /**
   * Should be true if this is the last item in the list
   */
  isLast: PropTypes.bool,

  /**
   * Callback to handle item reordering. Called with -1 or +1 depending on the requested reorder.
   */
  onReorder: PropTypes.func,

  /**
   * Category content to display
   */
  content: PropTypes.node,

  /**
   * Buttons to display in controls area
   */
  controls: PropTypes.node,

  /**
   * Number of subcategories below this category
   */
  subcategoriesCount: PropTypes.number,
  compactLayout: PropTypes.bool,

  /**
   * Function to render subcategories. Called to get content to display when expanded.
   */
  renderSubcategories: PropTypes.func,
};
